import ReactQuill from "react-quill-new";
import QuillTableBetter from "quill-table-better";
import QuillResize from "quill-resize-module";
import { createQuillImageHandler } from "./CreateImageHandler";
import { createQuillDocumentHandler } from "./CreateDocumentHandler";

const Quill = ReactQuill.Quill;

// ------------------------------
// Register modules ONCE (safe)
// ------------------------------
let __quillRegistered = false;

function ensureQuillPluginsRegistered() {
  // If you use Next.js SSR, keep this guard.
  if (typeof window === "undefined") return;

  if (__quillRegistered) return;
  __quillRegistered = true;

  // ✅ Size (inline-style px values)
  const SizeStyle = Quill.import("attributors/style/size") as any;
  SizeStyle.whitelist = [
    "8px", "10px", "12px", "14px", "16px", "18px",
    "20px", "24px", "26px", "28px", "32px", "40px", "48px", "72px",
  ];
  Quill.register(SizeStyle, true);

  // ✅ Font whitelist (your existing logic)
  const Font = Quill.import("attributors/class/font") as any;
  Font.whitelist = [
    "sans-serif",
    "serif",
    "monospace",
    "roboto",
    "lato",
    "open-sans",
    "inter",
    "poppins",
    "oswald",
    "montserrat",
  ];
  Quill.register(Font, true);

  // ✅ Table module (Quill 2)
  // - toolbar button: 'table-better'
  // - table resizing supported by plugin UI (drag corner / tools) :contentReference[oaicite:6]{index=6}
  try {
    Quill.register({ "modules/table-better": QuillTableBetter }, true);

    // Patch quill-table-better blots whose create() calls Object.keys(value)
    // without guarding against null/undefined. Parchment passes undefined when
    // wrapping an orphaned blot (e.g. TableCellBlock outside its TableCell),
    // which causes "Cannot convert undefined or null to object".
    const blotsToPatch = [
      "formats/table-cell",
      "formats/table-th",
      "formats/table-container",
      "formats/table-col",
    ];
    blotsToPatch.forEach((path) => {
      const BlotClass = Quill.import(path) as any;
      if (BlotClass && typeof BlotClass.create === "function") {
        const original = BlotClass.create;
        BlotClass.create = function (value: any) {
          return original.call(this, value ?? {});
        };
      }
    });
  } catch (e) {
    // ignore if hot-reload re-registers
  }

  // ✅ Resize module (Quill 2)
  // supports image resize / embedded element resize :contentReference[oaicite:7]{index=7}
  try {
    Quill.register("modules/resize", QuillResize as any);
  } catch (e) {
    // ignore if hot-reload re-registers
  }

  // ✅ Custom document upload icon
  const Icons = Quill.import("ui/icons") as any;
  Icons["document"] =
    `<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 1H4a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6l-5-5z" stroke="currentColor" stroke-width="1.2" fill="none"/>
      <path d="M10 1v5h5" stroke="currentColor" stroke-width="1.2" fill="none"/>
      <line x1="5" y1="9"  x2="13" y2="9"  stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="5" y1="12" x2="13" y2="12" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="5" y1="15" x2="9"  y2="15" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    </svg>`;
}

// ------------------------------
// Toolbar (add table-better button)
// ------------------------------
export const quillToolbar = [
  ["bold", "italic", "underline", "strike"],

  [
    {
      font: [
        "sans-serif",
        "serif",
        "monospace",
        "roboto",
        "lato",
        "open-sans",
        "inter",
        "poppins",
        "oswald",
        "montserrat",
      ],
    },
  ],

  [{ size: ["8px", "10px", "12px", "14px", "16px", "18px", "20px", "24px", "26px", "28px", "32px", "40px", "48px", "72px"] }],

  [{ header: [1, 2, 3, 4, 5, 6, false] }],

  [{ list: "ordered" }, { list: "bullet" }],

  [{ align: [] }],

  [{ script: "sub" }, { script: "super" }],

  ["blockquote"],

  [{ color: [] }, { background: [] }],

  ["link", "image", "video", "document"],

  // ✅ Table button (quill-table-better)
  ["table-better"],

  ["emoji"],

  [{ indent: "-1" }, { indent: "+1" }],
  [{ direction: "rtl" }],
];

// ------------------------------
// Modules builder
// ------------------------------
export const makeQuillModules = (editorRef: any, storage = "EVENTS") => {
  ensureQuillPluginsRegistered();

  return {
    // Disable Quill's built-in table module (table-better replaces it)
    table: false,

    toolbar: {
      container: quillToolbar,
      handlers: {
        image: createQuillImageHandler(editorRef, { storage }),
        document: createQuillDocumentHandler(editorRef, { storage }),
        // placeholder — CommonQuill intercepts via capture listener
        emoji: () => {},
        // Heading and size are mutually exclusive.
        // Clear size across the entire line (not just selection) so an inline
        // font-size on any part of the line doesn't visually override H1/H2.
        header: function (this: any, value: any) {
          if (value) {
            const sel = this.quill.getSelection();
            if (sel) {
              const [line] = this.quill.getLine(sel.index);
              if (line) {
                const lineStart = this.quill.getIndex(line);
                this.quill.formatText(lineStart, line.length(), "size", false, "user");
              }
            }
          }
          this.quill.format("header", value, "user");
        },
        size: function (this: any, value: any) {
          if (value) this.quill.format("header", false, "user");
          this.quill.format("size", value, "user");
        },
      },
    },

    // ✅ Table module config :contentReference[oaicite:8]{index=8}
    "table-better": {
      language: "en_US",
      toolbarTable: true, // adds the toolbar insert behavior
      menus: ["column", "row", "merge", "table", "cell", "delete"],
    },

    // Required keyboard bindings for table module :contentReference[oaicite:9]{index=9}
    keyboard: {
      bindings: (QuillTableBetter as any).keyboardBindings,
    },

    // ✅ Image/video/iframe resize module config :contentReference[oaicite:10]{index=10}
    resize: {
      modules: ["Resize", "DisplaySize", "Toolbar", "Keyboard"],
      tools: ["left", "center", "right", "full", "edit"],
      parchment: {
        image: {
          attribute: ["width"], // keeps it simple: width-only resizing
          limit: {
            minWidth: 80,
            maxWidth: 900,
          },
        },
        // Optional: allow resizing videos too
        video: {
          attribute: ["width", "height"],
          limit: { minWidth: 200, ratio: 0.5625 }, // 16:9
        },
      },
    },
  };
};